import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { mountSteps } from '../../data/mountSystem';
const EASE = [0.23, 1, 0.32, 1] as const;
const STEP_MS = 3400;
const AWAY = 250; // how far the detached light travels (SVG units)

/**
 * Three-step side-profile demonstration of the Magnetic Mount System:
 * the base never moves; the M1 PRO lifts off and snaps back to the same position.
 * Matched 3.3 in diameter; light 1.2 in thick, base slim. Profile view — no LED geometry is drawn.
 */
export function MountSwapAnimation({
  compact = false
}: {
  compact?: boolean;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    amount: 0.4
  });
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(true);
  useEffect(() => {
    if (!auto || reduce || !inView) return;
    const id = window.setInterval(() => setStep(s => (s + 1) % mountSteps.length), STEP_MS);
    return () => window.clearInterval(id);
  }, [auto, reduce, inView]);
  const choose = (i: number) => {
    setAuto(false);
    setStep(i);
  };
  const d = (s: number) => reduce ? 0 : s;
  // Light motion per step: 0 = base only, 1 = lift off, 2 = snap back on.
  const light = step === 0 ? {
    x: 0,
    opacity: 0,
    transition: {
      duration: d(0.2),
      ease: EASE
    }
  } : step === 1 ? {
    x: [0, 0, AWAY],
    opacity: 1,
    transition: {
      duration: d(0.75),
      times: [0, 0.6, 1],
      ease: EASE
    }
  } : {
    x: [AWAY, AWAY, 0],
    opacity: 1,
    transition: {
      duration: d(0.9),
      times: [0, 0.66, 1],
      ease: EASE
    }
  };
  return <div ref={ref} data-id="element-371" data-mp="2zpfzelt7x1ps">
      <figure className="mf-hud mf-tech-grid relative overflow-hidden border border-marine/25 bg-[#06121F]" data-id="element-372" data-mp="2zmtrum6bum7k">
        <p className="pointer-events-none absolute left-4 top-3 z-10 font-display text-[13px] font-semibold uppercase tracking-[0.2em] text-ice md:left-6 md:top-5 md:text-base" aria-hidden data-id="element-373" data-mp="34ln8uoktovch">
          Remove. Recharge. Reattach.
        </p>

        <svg viewBox="0 0 560 330" className="w-full" role="img" aria-labelledby="swap-title" data-id="element-374" data-mp="2dlfj7r6xoywl">
          <title id="swap-title" data-id="element-375" data-mp="28ndtinf061c5">
            {`Step ${step + 1} of 3: ${mountSteps[step].title} The magnetic base stays fixed on the surface while the M1 PRO light ${step === 1 ? 'is lifted off to recharge' : step === 2 ? 'snaps back onto the same base' : 'is not yet attached'}.`}
          </title>

          {/* Mounting surface (pool wall) */}
          <rect x="40" y="40" width="46" height="270" fill="#0E1B29" data-id="element-376" data-mp="2rjm1hyzegqb5" />
          {[70, 120, 170, 220, 270].map(y => <line key={y} x1="40" y1={y} x2="86" y2={y} stroke="#B8C6D3" strokeOpacity="0.12" data-id="element-377" data-mp="35bk55uacdtzx" />)}
          <line x1="86" y1="40" x2="86" y2="310" stroke="#B8C6D3" strokeOpacity="0.45" data-id="element-378" data-mp="31b9w28r96ngu" />

          {/* Fixed-position crosshair through the base centre */}
          <line x1="20" y1="175" x2="540" y2="175" stroke="#9CD8FF" strokeOpacity="0.14" strokeDasharray="3 7" data-id="element-379" data-mp="3aby0qcurbbh2" />

          {/* Blue light beam (only once seated and lit) */}
          <motion.path d="M156 128 L540 70 L540 280 L156 222 Z" fill="#248DFF" initial={false} animate={{
          opacity: step === 2 ? 0.16 : 0
        }} transition={{
          duration: d(0.3),
          delay: step === 2 ? d(0.85) : 0,
          ease: EASE
        }} data-id="element-380" data-mp="2qng1d8pnynu" />

          {/* Magnetic base — fixed. White ABS, 3.3 in diameter, slim profile. */}
          <motion.g initial={false} animate={{
          opacity: 1
        }} transition={{
          duration: d(0.25)
        }} data-id="element-381" data-mp="3b4dw0e49jylx">
            <rect x="86" y="120" width="16" height="110" rx="3" fill="#E7ECF1" data-id="element-382" data-mp="3192ddkpqi78w" />
            <rect x="99" y="132" width="3" height="86" fill="#B8C6D3" opacity="0.7" data-id="element-383" data-mp="3674852wivv07" />
          </motion.g>
          <motion.circle cx="94" cy="175" r="70" fill="none" stroke="#9CD8FF" initial={false} animate={step === 0 ? {
          opacity: [0, 0.5, 0],
          scale: [0.96, 1.08, 1.14]
        } : {
          opacity: 0
        }} transition={{
          duration: d(0.9),
          ease: EASE
        }} style={{
          transformOrigin: '94px 175px'
        }} data-id="element-384" data-mp="2s72vkq6waz4w" />

          {/* Magnetic field lines between base and light while detached */}
          <motion.g initial={false} animate={{
          opacity: step === 1 ? 1 : 0
        }} transition={{
          duration: d(0.25),
          delay: step === 1 ? d(0.5) : 0
        }} data-id="element-385" data-mp="2fdtzdo3jfk6b">
            {[0, 1, 2].map(i => <path key={i} className="mf-field" d={`M106 ${150 + i * 25} Q${200 + i * 6} ${150 + i * 25 + (i - 1) * 8} 300 ${150 + i * 25}`} fill="none" stroke="#248DFF" strokeOpacity={0.55 - Math.abs(i - 1) * 0.2} strokeDasharray="3 6" data-id="element-386" data-mp="26gtdbo3xxy61" />)}
          </motion.g>

          {/* M1 PRO light — 1.2 in thick, same diameter as the base */}
          <motion.g initial={false} animate={light} data-id="element-387" data-mp="3lxk3f7e8fte3">
            <rect x="102" y="120" width="52" height="110" rx="16" fill="#F4F7FA" data-id="element-388" data-mp="3ud9fvrcc2x07" />
            <rect x="102" y="120" width="10" height="110" rx="3" fill="#D6DEE6" data-id="element-389" data-mp="3ubgf2soej704" />
            <motion.rect x="150" y="128" width="6" height="94" rx="3" fill="#248DFF" initial={false} animate={{
            opacity: step === 2 ? 1 : 0.25
          }} transition={{
            duration: d(0.2),
            delay: step === 2 ? d(0.8) : 0
          }} data-id="element-390" data-mp="27npr8j2gyga" />
            {/* USB-C cover (blue) on the rear */}
            <rect x="114" y="212" width="12" height="6" rx="2" fill="#248DFF" data-id="element-391" data-mp="3fi356r2ama0j" />
          </motion.g>

          {/* Snap confirmation pulse */}
          <motion.circle cx="128" cy="175" r="66" fill="none" stroke="#9CD8FF" strokeWidth="2" initial={false} animate={step === 2 ? {
          opacity: [0, 0, 0.8, 0],
          scale: [0.96, 0.96, 1.1, 1.25]
        } : {
          opacity: 0
        }} transition={{
          duration: d(1.2),
          times: [0, 0.66, 0.8, 1],
          ease: EASE
        }} style={{
          transformOrigin: '128px 175px'
        }} data-id="element-392" data-mp="3pepqfgyj7333" />

          {/* Charging indicator next to the detached light */}
          <motion.g initial={false} animate={{
          opacity: step === 1 ? 1 : 0
        }} transition={{
          duration: d(0.25),
          delay: step === 1 ? d(0.75) : 0
        }} data-id="element-393" data-mp="owy5lxcvf1tl">
            <rect x="358" y="252" width="62" height="24" rx="5" fill="none" stroke="#B8C6D3" strokeOpacity="0.7" data-id="element-394" data-mp="jx25wab7hwju" />
            <rect x="420" y="259" width="4" height="10" rx="1" fill="#B8C6D3" fillOpacity="0.7" data-id="element-395" data-mp="3nfvd85de5p5k" />
            <motion.rect x="362" y="256" height="16" rx="3" fill="#248DFF" initial={false} animate={{
            width: step === 1 ? 54 : 12
          }} transition={{
            duration: d(0.3),
            delay: step === 1 ? d(1.4) : 0,
            ease: EASE
          }} data-id="element-396" data-mp="5yve9bwuossm" />
            <text x="432" y="269" fill="#B8C6D3" fontSize="12" fontFamily="Inter" data-id="element-397" data-mp="34occk56uan6p">USB-C recharge</text>
          </motion.g>

          {/* Labels */}
          <text x="94" y="104" textAnchor="middle" fill="#FFFFFF" fontSize="13" fontFamily="Barlow Condensed" fontWeight="600" letterSpacing="1.5" data-id="element-398" data-mp="1oxtalwim1qih">
            {step === 1 ? 'BASE STAYS PUT' : step === 2 ? 'SAME POSITION' : 'FIXED POSITION'}
          </text>
          <line x1="60" y1="120" x2="60" y2="230" stroke="#B8C6D3" strokeOpacity="0.4" data-id="element-399" data-mp="1f8jiignro7ud" />
          <text x="54" y="180" textAnchor="end" fill="#B8C6D3" fontSize="11" fontFamily="Inter" data-id="element-400" data-mp="22yvzeh0zpex3">Ø 3.3 in</text>
          <motion.text x="128" y="252" textAnchor="middle" fill="#9CD8FF" fontSize="13" fontFamily="Barlow Condensed" fontWeight="600" letterSpacing="1.5" initial={false} animate={{
          opacity: step === 2 ? 1 : 0
        }} transition={{
          duration: d(0.2),
          delay: step === 2 ? d(0.85) : 0
        }} data-id="element-401" data-mp="27w5lpnt31agp">
            LOCKED IN
          </motion.text>
        </svg>
      </figure>

      <ol className={`mt-4 grid gap-2 ${compact ? 'grid-cols-3' : 'grid-cols-3 md:gap-3'}`} data-id="element-402" data-mp="1e8gy2np0kw44">
        {mountSteps.map((s, i) => {
        const active = i === step;
        return <li key={s.id} data-id="element-403" data-mp="1570m4bijwlxs" data-mpi={i}>
              <button type="button" onClick={() => choose(i)} aria-pressed={active} aria-label={`Step ${i + 1}: ${s.title}`} className={`flex h-full w-full flex-col border-t-2 pt-3 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice ${active ? 'border-marine text-white' : 'border-white/15 text-titanium hover:text-white'}`} data-id="element-404" data-mp="158br2enqfzqh">
                <span className="font-display text-xs font-semibold text-marine" data-id="element-405" data-mp="2glmk50i1lslb">{i + 1}</span>
                <span className="font-display text-base font-semibold uppercase leading-tight tracking-[0.04em] md:text-lg" data-id="element-406" data-mp="2bu4momf8aa53">{s.title}</span>
                {!compact && <span className="mt-1 hidden text-sm text-titanium md:block" data-id="element-407" data-mp="2j7x2wl63g2gx">{s.body}</span>}
              </button>
            </li>;
      })}
      </ol>
      <p className="sr-only" aria-live="polite" data-id="element-408" data-mp="2t2xa980i5eio">
        {mountSteps[step].title} {mountSteps[step].body}
      </p>
    </div>;
}